/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FormikHelpers, useFormik } from 'formik';
import React, { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { unstable_usePrompt } from 'react-router';

import { Button, FormControl, FormHelperText, InputLabel, OutlinedInput, Typography } from '@mui/material';

import { useUpdateProject } from '../../../../components/hooks/useUpdateProject';
import { Namespaces } from '../../../../i18n';
import { useFormStyles } from '../../../../styles/form';
import { ApiClientError } from '../../../../types/api/ApiClientError';
import { EntityId } from '../../../../types/api/EntityId';
import { IProject } from '../../../../types/project/IProject';
import { UpdateProjectRequestBody } from '../../../../types/project/UpdateProjectRequestBody';
import { reactToUpdateProject } from '../../../../utils/reactions';
import { useApiValidation } from '../../../../utils/useApiValidation';
import { useIsMounted } from '../../../../utils/useIsMounted';
import { useValidationTranslation } from '../../../../utils/useValidationTranslation';
import { validationSchema } from './EditProjectForm.consts';
import { useEditProjectFormStyles } from './EditProjectForm.styles';

type EditProjectFormProps = {
  organizationId: EntityId;
  project: IProject;
};

const getInitialValues = (project?: IProject): UpdateProjectRequestBody => {
  if (project) {
    return {
      name: project.name,
      description: project.description,
    };
  }
  return {
    name: '',
    description: '',
  };
};

export const EditProjectForm = memo<EditProjectFormProps>(props => {
  const { organizationId, project } = props;
  const classes = useEditProjectFormStyles();
  const formClasses = useFormStyles();
  const { t } = useTranslation([Namespaces.Cluster, Namespaces.Common]);
  const { vtr } = useValidationTranslation();
  const { getFieldApiError, setApiError, resetApiError, resetFieldApiError } = useApiValidation();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isMounted = useIsMounted();

  const [updateProject] = useUpdateProject(true);

  const handleEditSubmit = async (
    values: UpdateProjectRequestBody,
    helpers: FormikHelpers<UpdateProjectRequestBody>,
  ) => {
    try {
      setIsLoading(true);
      resetApiError();
      const data: UpdateProjectRequestBody = {
        name: values.name?.trim(),
        description: values.description?.trim() || null,
      };
      const result = await updateProject({
        projectId: project.id,
        organizationId,
        data,
      });
      if (!isMounted.current) {
        return;
      }
      helpers.setSubmitting(false);
      helpers.resetForm({ values: getInitialValues(result?.data.payload) });
      void reactToUpdateProject({
        organizationId,
        projectId: project.id,
      });
    } catch (e) {
      if (!isMounted.current) {
        return;
      }
      const error = e as ApiClientError;
      if (error.response) {
        setApiError(error);
      }
    } finally {
      if (!isMounted.current) {
        // eslint-disable-next-line no-unsafe-finally
        return;
      }
      setIsLoading(false);
    }
  };

  const initialValues = useMemo(() => getInitialValues(project), [project]);

  const formik = useFormik<UpdateProjectRequestBody>({
    validationSchema: validationSchema,
    initialValues,
    onSubmit: handleEditSubmit,
  });

  const { values, touched, errors, handleChange, setFieldTouched } = formik;

  const change = (name: keyof UpdateProjectRequestBody, e: React.ChangeEvent) => {
    e.persist();
    handleChange(e);
    resetFieldApiError(name);
    void setFieldTouched(name, true, false);
  };

  const getError = (name: keyof UpdateProjectRequestBody): string => {
    return errors[name] || getFieldApiError(name) || '';
  };

  const hasError = (name: keyof UpdateProjectRequestBody): boolean | undefined => {
    return touched[name] && !!getError(name);
  };

  unstable_usePrompt({
    when: formik.dirty,
    message: t(`${Namespaces.Common}:confirmation.leave_dirty_form`),
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Typography classes={{ h4: classes.title }} variant="h4" component="h4">
        {t(`${Namespaces.OrganizationAdmin}:management.details_section.general_title`)}
      </Typography>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthFull, classes.formControl)}
        error={hasError('name')}
        variant="outlined"
      >
        <InputLabel htmlFor="name">{t('name')}</InputLabel>
        <OutlinedInput name="name" value={values.name} label={t('name')} onChange={change.bind(null, 'name')} />
        {hasError('name') && (
          <FormHelperText>
            {vtr(getError('name'), {
              field: t('name'),
              maxLength: 50,
            })}
          </FormHelperText>
        )}
      </FormControl>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthFull, classes.formControl)}
        error={hasError('description')}
        variant="outlined"
      >
        <InputLabel htmlFor="description">{t('description')}</InputLabel>
        <OutlinedInput
          name="description"
          label={t('description')}
          value={values.description || ''}
          multiline
          onChange={change.bind(null, 'description')}
        />
        {hasError('description') && (
          <FormHelperText>
            {vtr(getError('description'), {
              field: t('description'),
              maxLength: 200,
            })}
          </FormHelperText>
        )}
      </FormControl>
      <div className={classes.buttons}>
        <div className={classes.buttonWrap}>
          <Button variant="contained" type="submit" disabled={isLoading || !formik.dirty}>
            {t(`${Namespaces.Common}:form.save`)}
          </Button>
        </div>
        <div className={classes.buttonWrap}>
          <Button onClick={() => formik.resetForm()} variant="outlined" disabled={isLoading || !formik.dirty}>
            {t(`${Namespaces.Common}:form.cancel`)}
          </Button>
        </div>
      </div>
    </form>
  );
});
